import { observer } from 'mobx-react-lite'
import { components as reactSelectComponents } from 'react-select'
import { CustomOptionProps } from '@evertel/web/ui'

// evertel
import {
    Icon,
    Text,
    FloatingMenu,
    MenuItem,
    useUI
} from '@evertel/web/ui'
import { UserTileRawData } from '@evertel/web/user'
import { APIDataBlueUser, APIDataRoom } from '@evertel/types'
import { useContext, useEffect, useState } from 'react'
import { useService } from '@evertel/di'
import { CurrentUserController, useUserTagline } from '@evertel/blue-user'
import { RoomContext } from '../RoomContext'

interface SelectCustomProps {
    room: APIDataRoom
    openUserProfile: (userId: number) => void
    isCurrentUserGuest: boolean
}

const SelectUserDisplay = observer((props: CustomOptionProps<APIDataBlueUser, SelectCustomProps>) => {
    const { data: user, selectProps, innerRef } = props
    const {
        room,
        openUserProfile,
        isCurrentUserGuest
    } = selectProps.customProps

    const { isConfirmed, addToast } = useUI()

    const { roomController, canManageRoom } = useContext(RoomContext)

    const currentUserController = useService(CurrentUserController, [])

    const isManager = roomController?.managerIds?.includes(user.id)
    const isMember = roomController?.memberIds?.includes(user.id)

    const [hasInternalFocus, setHasInternalFocus] = useState(false)
    const [optionElement, setOptionElement] = useState<HTMLDivElement | null>(null)

    const handleRef = (element: HTMLDivElement | null) => {
        setOptionElement(element)
        if (typeof innerRef === 'function') {
            innerRef(element)
        }
    }

    useEffect(() => {
        const checkFocus = () => {
            if (optionElement && document.activeElement) {
                setHasInternalFocus(optionElement.contains(document.activeElement))
            }
        }

        // Check focus on mount and when focus changes anywhere in the document
        checkFocus()
        document.addEventListener('focusin', checkFocus)
        document.addEventListener('focusout', checkFocus)

        return () => {
            document.removeEventListener('focusin', checkFocus)
            document.removeEventListener('focusout', checkFocus)
        }
    }, [optionElement])

    const handleMouseEnter = () => {
        const focusedElement = document.activeElement as HTMLElement

        if (focusedElement.tagName.toLowerCase() !== 'input') {
            //is something else in the select is focused, remove that focus
            focusedElement.blur()
            if (optionElement) {
                optionElement.focus()
            }
        }
        setHasInternalFocus(true)
    }

    const handleMouseLeave = () => {
        // document.activeElement.blur()
        setHasInternalFocus(false)
    }

    const { displayPosition, isGuest: isUserGuest } = useUserTagline({
        departmentsAccess: user.departmentsAccess || [],
        contextDepartmentId: room?.departmentId,
        reportingDepartment: user.reportingDepartment
    })

    // this allows us to update the list without triggering
    // a new search when users are removed
    if (!isManager && !isMember) return null

    const isArchived = room?.isArchived
    const isAgencyWideRoom = room?.options?.autoAddDepartmentMembers

    // if there are no managers, then an agency manager can add a new manager
    const canManageOrphanedRoom = roomController?.managerIds?.length === 0 && currentUserController.canManageInDepartment(room?.departmentId)

    // Permission checks matching AddRoomMembersModal
    const canManageRoomMembers = (canManageRoom || canManageOrphanedRoom) && !isArchived && !isCurrentUserGuest
    const canRemove = (isMember || isManager) && (!isAgencyWideRoom || (isAgencyWideRoom && isUserGuest))

    const removeUser = async () => {
        try {
            const confirmed = await isConfirmed({
                message: 'Are you sure you want to remove this user?',
                acceptButton: {
                    label: 'Remove',
                    color: 'danger'
                }
            })
            if (!confirmed) return

            await roomController.removeUser(user.id)

        } catch (error) {
            addToast({
                color: 'danger',
                message: error.message
            })
        }
    }

    const updateRole = async () => {
        try {
            if (isManager) {
                await roomController?.addMember(user.id)
            } else {
                roomController?.addManager(user.id)
            }
        } catch (error) {
            addToast({
                color: 'danger',
                message: error.message
            })
        }
    }

    const isFocused = hasInternalFocus

    const newProps = {
        ...props,
        isFocused,
        innerRef: handleRef
    }
    newProps.innerProps = {
        ...newProps.innerProps,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave
    }

    return (
        <reactSelectComponents.Option {...newProps}>
            <UserTileRawData
                user={user}
                imageWidth={40}
                className="mb-1 directory-list-item"
                isManager={isManager}
                isGuest={isUserGuest}
                subComponent={
                    <Text
                        size="small"
                        className="text-truncate">
                        {displayPosition}
                    </Text>
                }
                rightColComponent={
                    <FloatingMenu
                        label="User Options"
                        className="item-options-btn px-3"
                        renderLabel={() => (
                            <Icon
                                name="ellipsis-v"
                            />
                        )}
                    >
                        <MenuItem
                            label="View User"
                            onClick={() => openUserProfile(user.id)}
                        />
                        {canManageRoomMembers && (
                            <MenuItem
                                label={isManager ? 'Make room Member' : 'Make room Manager'}
                                onClick={updateRole}
                            />
                        )}
                        {canManageRoomMembers && canRemove && (
                            <MenuItem
                                label="Remove from room"
                                onClick={removeUser}
                                className="text-danger"
                            />
                        )}

                    </FloatingMenu>
                }
            />
        </reactSelectComponents.Option>
    )
})

export { SelectUserDisplay }
